import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { CloudOff, RotateCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/ui/primitives';
import { isChunkLoadError } from './lazy-route';

/**
 * The last thing between a failed screen and an empty <main>.
 *
 * Without a boundary here, any error thrown while a route renders — including
 * the rejection of a chunk that could not be fetched — unwinds to the root and
 * React removes the whole tree. Production showed that as a page with no text
 * on it at all, and one console line the person looking at the blank screen
 * could not see.
 *
 * It sits INSIDE AppShell on purpose. The shell and its navigation are not what
 * failed, and leaving them mounted means the way out is a menu item rather than
 * a reload: moving to another route changes `pathname`, which remounts this
 * boundary with clean state and mounts a different lazy component.
 *
 * Reloading is offered rather than performed. An automatic reload is a loop
 * whenever the cause is not transient — a chunk deleted by a rollback answers
 * 404 every time — and this screen already comes after three failed attempts
 * inside the loader. index.html is served `no-cache`, so a reload does pick up
 * a new build's chunk names, which is the other thing this can be.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  // `key` rather than a resetKey prop and derived state: a changed key gives a
  // fresh instance, which is the whole reset. The subtree it remounts is
  // <Suspense><Routes>, which re-renders on every navigation anyway.
  return <RouteErrorBoundaryImpl key={pathname}>{children}</RouteErrorBoundaryImpl>;
}

interface State {
  error: Error | null;
}

class RouteErrorBoundaryImpl extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept in the console for a support ticket to quote. Nothing is reported
    // anywhere else: this app has no error sink, and inventing one here would
    // be a second mechanism for a question nobody has asked yet.
    console.error('[route]', error, info.componentStack);
  }

  private reload = () => window.location.reload();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const offline = isChunkLoadError(error);

    return (
      <div role="alert" className="mx-auto max-w-md py-16 text-center">
        {offline ? (
          <CloudOff className="text-fg-muted mx-auto mb-3" size={28} />
        ) : (
          <TriangleAlert className="text-fg-muted mx-auto mb-3" size={28} />
        )}

        <h1 className="text-fg text-lg font-semibold">
          {offline ? 'Sayfa yüklenemedi' : 'Bu ekran açılırken bir hata oluştu'}
        </h1>

        <p className="text-fg-muted mt-1 text-sm">
          {offline
            ? 'Bu ekranın dosyası indirilemedi. Bağlantınız kesilmiş ya da yeni bir sürüm yayınlanmış olabilir. Yenilemek çoğu durumda yeterlidir.'
            : 'Menüden başka bir ekrana geçebilir ya da sayfayı yenileyebilirsiniz. Sorun sürerse sistem yöneticinize bildirin.'}
        </p>

        <Button variant="primary" className="mt-5" onClick={this.reload}>
          <RotateCw size={16} />
          Sayfayı yenile
        </Button>

        {/* The technical line, closed by default. A person on a phone does not
            need it; whoever answers the support ticket does, and asking them to
            open a console on someone else's device is asking for nothing. */}
        <details className="mt-6 text-left">
          <summary className="text-fg-muted cursor-pointer text-xs">Teknik ayrıntı</summary>
          <p className="text-fg-muted border-border bg-surface-muted mt-2 overflow-x-auto rounded-lg border px-3 py-2 font-mono text-xs break-words">
            {error.message || error.name}
          </p>
        </details>
      </div>
    );
  }
}
